// Parallel blob fetch + JSZip packaging (no compression for speed).
import JSZip from "jszip";
import api from "@/lib/api";

const CONCURRENCY = 3;

/**
 * @param items Array<{ url, params?, filename, size? }>
 * @param zipName String — name of the resulting .zip
 * @param onProgress (pct: number, label: string) => void
 */
export async function downloadFilesAsZip(items, zipName, onProgress) {
  if (!items?.length) return { ok: false };
  const zip = new JSZip();
  const total = items.reduce((a, x) => a + (x.size || 0), 0) || 1;
  const progressByIndex = new Array(items.length).fill(0);
  let failedAt = null;

  const reportProgress = (currentLabel) => {
    const loaded = progressByIndex.reduce((a, b) => a + b, 0);
    const pct = Math.min(99, Math.round((loaded / total) * 100));
    onProgress?.(pct, currentLabel);
  };

  // Worker pool — each worker pulls the next index off the queue and fetches it.
  let cursor = 0;
  const worker = async () => {
    while (true) {
      const i = cursor++;
      if (i >= items.length || failedAt) return;
      const it = items[i];
      try {
        const resp = await api.get(it.url, {
          params: it.params,
          responseType: "blob",
          onDownloadProgress: (e) => {
            progressByIndex[i] = e.loaded || 0;
            reportProgress(`Fetching ${it.filename}`);
          },
        });
        progressByIndex[i] = it.size || resp.data.size || 0;
        zip.file(it.filename, resp.data);
      } catch {
        failedAt = it.filename;
        return;
      }
    }
  };

  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, items.length) }, worker));
  if (failedAt) return { ok: false, failedAt };

  onProgress?.(99, "Packing zip…");
  // STORE = no compression. Files inside a ZIP this way are concatenated, not deflated.
  // 95% of real-world transfers are media/PDFs/archives that are ALREADY compressed,
  // so DEFLATE wastes CPU for minimal size win. STORE is dramatically faster.
  const blob = await zip.generateAsync({ type: "blob", compression: "STORE" });
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = zipName.endsWith(".zip") ? zipName : `${zipName}.zip`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
  onProgress?.(100, "Done");
  return { ok: true };
}
